/**
 * ## Usage
 *
 * ### esm
 *
 * ```js
 * import { create } from '@paychex/oidc';
 * ```
 *
 * ### cjs
 *
 * ```js
 * const { create } = require('@paychex/oidc');
 * ```
 *
 * ### amd
 *
 * ```js
 * define(['@paychex/oidc'], function({ create }) { ... });
 * require(['@paychex/oidc'], function({ create }) { ... });
 * ```
 *
 * ### iife
 *
 * ```js
 * const { create } = window['@paychex/oidc'];
 * ```
 *
 * @module main
 */

import { xhr } from '@paychex/adapter-xhr';
import { data, signals } from '@paychex/core';
import { crossOrigin } from '@paychex/platform-browser';

import factory from './oidc';

/**
 * Provides Open ID Connect (OIDC) functionality to Paychex JavaScript applications.
 *
 * @example
 * ```js
 * const oidc = await create(oidc.authority_url, oidc.client_id, cross_origin_iframe_url, verifier, challenge, audience, connection, with_creds);
 * ```
 */
export interface OIDC {

    /**
     * Retrieves the user's authorization token.
     *
     * @param scopes Optional scopes to request.
     * @param flexuser Optional flexuser to request.
     * @returns The currently logged in user's authorization token.
     * @example
     * ```js
     * const auth_token = await oidc.authorize(['flex-ui']);
     * ```
     */
    authorize(scopes?: string[], flexuser?: string, generatedCodeChallenge?: string): Promise<string>

    /**
     * Retrieves the user's access token.
     *
     * @param auth_token The user's authorization token, retrieved by calling {@link authorize}.
     * @returns The currently logged in user's access token.
     * @example
     * ```js
     * const auth_token = await oidc.authorize(['flex-ui', 'offline_access']);
     * const access_token = await oidc.token(auth_token);
     * ```
     */
    token(auth_token: string, generatedVerifier?: string): Promise<string>

}

/**
 * Constructs an OIDC API Paychex consumers can use.
 *
 * @param authority_url The OIDC authority URL.
 * @param client_id The client id registered with the OIDC authority.
 * @param iframe_uri The URL of the oidc-cross-origin index.html.
 * Not needed if your OIDC client configuration has only 1 silent redirect URL.
 * @returns An OIDC API Paychex consumers can use.
 * @example
 * ```js
 * const oidc = await create(oidc.authority_url, oidc.client_id, cross_origin_iframe_url, verifier, challenge);
 * ```
 */
export async function create(authority_url: string, client_id: string, iframe_uri?: string, verifier?: string, challenge?: string, audience?: string, connection?: string, with_creds?: string): Promise<OIDC> {

    const oidc = factory({
        xhr,
        data,
        signals,
        crossOrigin,
    });

    return oidc(authority_url, client_id, iframe_uri, verifier, challenge, audience, connection, with_creds);

}